import React from "react";
import PropTypes from "prop-types";
import Card from "shared/Card";
import { formatCents } from "shared/@utils/money";
import MoneyInput from "shared/MoneyInput";
import CollapseToggle from "../CollapseToggle";
import styles from "./PricingCard.module.css";

/**
 * Product-level pricing card.
 *
 * Three rows:
 *  - Last Purchase Price (read-only, NetSuite-owned)
 *  - Premier Member Price (editable)
 *  - Retail Price (editable)
 *
 * Price provenance: NetSuite owns the price and the API reads it live from SQL
 * (`netsuitePricing`) — that is what the member is actually charged. The values
 * in `pricing` are the admin's *override* of it. So an empty input means "use
 * NetSuite's price", and a filled one means "override it". Each row shows the
 * NetSuite figure underneath, and an overridden row offers a reset.
 *
 * Money is stored as integer cents (`*Cents` keys).
 */
export function PricingCard({ pricing, netsuitePricing, onChange, currency }) {
  const safe = pricing || {};
  const base = netsuitePricing || null;
  const [collapsed, setCollapsed] = React.useState(false);

  const emit = (patch) => {
    onChange?.({
      lastPurchaseCents: safe.lastPurchaseCents ?? null,
      premierMemberCents: safe.premierMemberCents ?? null,
      retailCents: safe.retailCents ?? null,
      // Preserve the bundle price even though this card doesn't edit it: the
      // parent replaces the whole pricing object on change, so omitting
      // bundledCents here would silently drop a bundle's price when a
      // non-bundle price row is edited, and would also make reverting a price
      // edit read as a phantom unsaved change (baseline still carries the key).
      bundledCents: safe.bundledCents ?? null,
      ...patch,
    });
  };

  // The distributor cost is read-only and warehouse-owned, so it is read from
  // the SQL base rather than from `pricing`. `pricing` is the override store the
  // editor writes back, and this row has no input — sourcing it from there is
  // what let a seeded placeholder be saved back as if an admin had set it.
  const lastPurchaseCents =
    base && typeof base.lastPurchaseCents === "number"
      ? base.lastPurchaseCents
      : safe.lastPurchaseCents;

  const lastPurchaseDisplay =
    lastPurchaseCents === null || lastPurchaseCents === undefined
      ? "—"
      : `$${formatCents(lastPurchaseCents, currency)}`;

  /**
   * The NetSuite figure for a row, plus a reset when the admin has overridden
   * it. Renders nothing when this product has no NetSuite price at all
   * (admin-authored items and bundles have no Store.Products row).
   */
  const renderSource = (field, baseCents) => {
    if (typeof baseCents !== "number") return null;
    const overridden = typeof safe[field] === "number";
    if (!overridden) return null;
    return (
      <div className={styles.sourceRow} data-testid={`pricing-source-${field}`}>
        <button
          type="button"
          className={styles.resetButton}
          onClick={() => emit({ [field]: null })}
        >
          Reset
        </button>
      </div>
    );
  };

  return (
    <Card className={styles.card}>
      <div className={styles.header}>
        <span className={styles.title}>Pricing</span>
        <CollapseToggle
          collapsed={collapsed}
          onToggle={() => setCollapsed((v) => !v)}
        />
      </div>
      {!collapsed && (
        <>
          <div className={styles.divider} />

          <div className={styles.row}>
            <span className={`${styles.label} ${styles.lastPurchaseLabel}`}>
              Last Purchase Price:
            </span>
            <span
              className={styles.readonly}
              data-testid="pricing-last-purchase"
            >
              {lastPurchaseDisplay}
            </span>
          </div>

          <div className={styles.row}>
            <label className={styles.label} htmlFor="pricing-pm">
              Premier Member Price
            </label>
            <div className={styles.valueGroup}>
              <span className={styles.dollar}>$</span>
              <MoneyInput
                id="pricing-pm"
                value={safe.premierMemberCents}
                onChange={(cents) => emit({ premierMemberCents: cents })}
                placeholder={
                  base && typeof base.premierMemberCents === "number"
                    ? formatCents(base.premierMemberCents, currency)
                    : "00.00"
                }
              />
            </div>
          </div>
          {renderSource("premierMemberCents", base && base.premierMemberCents)}

          <div className={styles.row}>
            <label className={styles.label} htmlFor="pricing-retail">
              Retail Price
            </label>
            <div className={styles.valueGroup}>
              <span className={styles.dollar}>$</span>
              <MoneyInput
                id="pricing-retail"
                value={safe.retailCents}
                onChange={(cents) => emit({ retailCents: cents })}
                placeholder={
                  base && typeof base.retailCents === "number"
                    ? formatCents(base.retailCents, currency)
                    : "00.00"
                }
              />
            </div>
          </div>
          {renderSource("retailCents", base && base.retailCents)}
        </>
      )}
    </Card>
  );
}

PricingCard.propTypes = {
  pricing: PropTypes.shape({
    lastPurchaseCents: PropTypes.number,
    premierMemberCents: PropTypes.number,
    retailCents: PropTypes.number,
  }),
  netsuitePricing: PropTypes.shape({
    lastPurchaseCents: PropTypes.number,
    premierMemberCents: PropTypes.number,
    retailCents: PropTypes.number,
  }),
  onChange: PropTypes.func,
  currency: PropTypes.string,
};

PricingCard.defaultProps = {
  pricing: null,
  netsuitePricing: null,
  onChange: undefined,
  currency: "USD",
};

export default PricingCard;
